import type { UpsertRevenueGoalInput } from '@sea-snag/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { revenueGoalsApi } from '../lib/api-client';
import { queryKeys } from '../lib/query-keys';

export function useRevenueGoals(orgPid: string, year: number) {
  return useQuery({
    queryKey: queryKeys.revenueGoals.list(orgPid, year),
    queryFn: () => revenueGoalsApi.list(year),
    enabled: !!orgPid,
  });
}

// Caller is responsible for invalidating after batches complete — see Save All
// in the goals settings page. Per-mutation invalidation would clobber later
// dirty rows during Promise.allSettled.
export function useUpsertRevenueGoal() {
  return useMutation({
    mutationFn: (args: { boatPid: string; data: UpsertRevenueGoalInput }) =>
      revenueGoalsApi.upsert(args.boatPid, args.data),
  });
}

/** Fills `year`'s missing goals with the year before's targets. */
export function useCopyRevenueGoals(orgPid: string, year: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => revenueGoalsApi.copy(year - 1, year),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.revenueGoals.list(orgPid, year) });
      void qc.invalidateQueries({
        queryKey: queryKeys.analytics.revenueByBoat(orgPid, String(year)),
      });
    },
  });
}

export function useDeleteRevenueGoal(orgPid: string, year: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (boatPid: string) => revenueGoalsApi.remove(boatPid, year),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.revenueGoals.list(orgPid, year) });
      void qc.invalidateQueries({
        queryKey: queryKeys.analytics.revenueByBoat(orgPid, String(year)),
      });
    },
  });
}
